/**
 * User Alerts Route - React Router v7
 *
 * Route: /user/alerts
 * Description: User saved searches/alerts management
 */
import type {LoaderFunctionArgs} from 'react-router-dom'
/* eslint-disable @typescript-eslint/restrict-template-expressions, @typescript-eslint/no-throw-literal, @typescript-eslint/strict-boolean-expressions, @typescript-eslint/prefer-nullish-coalescing, @typescript-eslint/no-unused-vars */
import {useLoaderData} from 'react-router-dom'

import UserAlerts from './index.js'
import {enrichUserAlertsWithVehicleNames, getMakesWithModelsOptions} from './utils.js'

// Import styles
import './styles/index.scss'

/**
 * Loader for User Alerts page
 * Fetches user alerts, makes/models, SEO data, and tracking data
 */
export async function loader({context, request}: LoaderFunctionArgs) {
  const {domain, i18n} = context
  const {culture} = i18n
  const url = new URL(request.url)
  const path = url.pathname

  try {
    const config = domain.get('config')
    const {FILTER_NAMES} = config.get('searchFilters')

    // Step 1: Get page name
    const pageNameResult = await domain.get('get_page_name_page_use_case').execute({path})
    const pageName = pageNameResult?.pageName || 'user_alerts'

    // Step 2: Fetch all data in parallel
    const [userAlertsResult, pageTrackingResult, seoDataResult, makesWithModelsResult] = await Promise.allSettled([
      domain.get('get_user_alerts_use_case').execute(),
      domain.get('get_page_tracking_data_use_case').execute({page: pageName}),
      domain.get('get_seo_data_use_case').execute({culture, pageName}),
      domain.get('get_makes_with_models_vehicles_use_case').execute()
    ])

    // Extract user alerts
    const userAlertsError = !!(userAlertsResult.status === 'rejected' || userAlertsResult.value?.[0])
    const userAlertsResponse =
      userAlertsResult.status === 'fulfilled' && userAlertsResult.value?.[1] ? userAlertsResult.value[1] : null

    // Extract makes with models
    const makesWithModels =
      makesWithModelsResult.status === 'fulfilled' && makesWithModelsResult.value?.[1]
        ? makesWithModelsResult.value[1]?.value
        : null

    // Enrich user alerts with vehicle names
    const enrichedUserAlerts = enrichUserAlertsWithVehicleNames(userAlertsResponse?.userAlerts || [], makesWithModels)

    // Extract SEO data
    const seoDataError = !!(seoDataResult.status === 'rejected' || seoDataResult.value?.[0])
    const helmet =
      seoDataResult.status === 'fulfilled' && seoDataResult.value?.[1]?.head ? seoDataResult.value[1].head : {}

    // Extract tracking data
    const pageTrackingData = pageTrackingResult.status === 'fulfilled' ? pageTrackingResult.value : {}

    // Build filter options
    const listFiltersOptions = {
      [FILTER_NAMES.VEHICLES]: {
        options: getMakesWithModelsOptions({makesWithModels})
      }
    }

    return {
      helmet,
      listFiltersOptions,
      pageName,
      pageTrackingData,
      seoDataError,
      userAlerts: enrichedUserAlerts,
      userAlertsError
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[UserAlerts] Loader error:', error)
    return {
      helmet: {},
      listFiltersOptions: {},
      pageName: 'user_alerts',
      pageTrackingData: {},
      seoDataError: true,
      userAlerts: [],
      userAlertsError: true
    }
  }
}

/**
 * Component wrapper
 * Uses existing UserAlerts component
 */
export function Component() {
  const data = useLoaderData()
  return <UserAlerts {...data} />
}

export default {loader, Component}
